import React from 'react';
import {
  Box,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import { Email as EmailIcon } from '@mui/icons-material';

/** Kept in sync with the `comment` max on excelNotificationBody in @carboncare/schemas. */
export const COMMENT_MAX_LENGTH = 1000;

/** Split a free-text address field on commas/semicolons into trimmed addresses. */
export const parseEmailList = (value: string): string[] =>
  value
    .split(/[,;]/)
    .map((email) => email.trim())
    .filter((email) => email.includes('@'));

/**
 * De-duplicate addresses case-insensitively, keeping first-seen casing.
 * Mirrors dedupeEmails in the backend's utils/emailRecipients.ts.
 */
export const dedupeEmails = (emails: string[]): string[] => {
  const seen = new Set<string>();
  return emails.filter((email) => {
    const key = email.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export interface EmailNotificationDialogProps {
  /** Whether the dialog is currently open */
  open: boolean;
  /** Called when the dialog should close (Cancel click or after successful send) */
  onClose: () => void;
  /** Name of the file shown in the info alert, or null/undefined to hide the alert */
  fileName: string | null | undefined;
  /** Company users list fetched for the selected file */
  companyUsers: Array<{ id: number; firstName: string; lastName: string; email: string }>;
  /** Currently selected user IDs */
  selectedUserIds: number[];
  /** Called when the selected user IDs list changes */
  onSelectedUserIdsChange: (ids: number[]) => void;
  /** Current value of the additional-addresses textarea */
  customEmails: string;
  /** Called when the additional-addresses textarea changes */
  onCustomEmailsChange: (value: string) => void;
  /** Optional note included in the email body */
  comment: string;
  /** Called when the comment textarea changes */
  onCommentChange: (value: string) => void;
  /** Whether the send is in progress */
  sending: boolean;
  /** Called when the Send Email button is clicked */
  onSend: () => void;
}

/**
 * Shared email-notification dialog used by CalculatedFilesPage and FinalFilesPage.
 * Selected users and typed addresses are combined, not exclusive — the summary shows
 * the de-duplicated union, which is exactly what the backend will mail.
 */
const EmailNotificationDialog: React.FC<EmailNotificationDialogProps> = ({
  open,
  onClose,
  fileName,
  companyUsers,
  selectedUserIds,
  onSelectedUserIdsChange,
  customEmails,
  onCustomEmailsChange,
  comment,
  onCommentChange,
  sending,
  onSend,
}) => {
  const selectedUserEmails = companyUsers
    .filter((u) => selectedUserIds.includes(u.id))
    .map((u) => u.email);
  const finalRecipients = dedupeEmails([...selectedUserEmails, ...parseEmailList(customEmails)]);

  return (
    <Dialog
      open={open}
      onClose={() => !sending && onClose()}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Send Email Notification</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {fileName && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Sending notification for: <strong>{fileName}</strong>
            </Alert>
          )}

          <Typography variant="subtitle2" gutterBottom>
            Licensee Users ({companyUsers.length})
          </Typography>

          {companyUsers.length > 0 ? (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, px: 1 }}>
                <Checkbox
                  checked={selectedUserIds.length === companyUsers.length}
                  indeterminate={selectedUserIds.length > 0 && selectedUserIds.length < companyUsers.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onSelectedUserIdsChange(companyUsers.map((u) => u.id));
                    } else {
                      onSelectedUserIdsChange([]);
                    }
                  }}
                  size="small"
                />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Select All
                </Typography>
              </Box>
              <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <List dense>
                  {companyUsers.map((u) => (
                    <ListItem key={u.id} sx={{ py: 0 }}>
                      <Checkbox
                        checked={selectedUserIds.includes(u.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            onSelectedUserIdsChange([...selectedUserIds, u.id]);
                          } else {
                            onSelectedUserIdsChange(selectedUserIds.filter(id => id !== u.id));
                          }
                        }}
                        size="small"
                      />
                      <ListItemText
                        primary={`${u.firstName} ${u.lastName}`}
                        secondary={u.email}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No users found for this licensee. Users may not have been added yet.
            </Typography>
          )}

          <Divider sx={{ my: 2 }} />

          <TextField
            fullWidth
            multiline
            rows={2}
            label="Additional Email Addresses"
            placeholder="Enter email addresses separated by commas or semicolons"
            value={customEmails}
            onChange={(e) => onCustomEmailsChange(e.target.value)}
            helperText="Example: user1@example.com, user2@example.com"
          />

          {finalRecipients.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Will send to ({finalRecipients.length}): {finalRecipients.join(', ')}
            </Typography>
          )}

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Message (optional)"
            placeholder="Add a short comment about the file being shared"
            value={comment}
            onChange={(e) => onCommentChange(e.target.value)}
            inputProps={{ maxLength: COMMENT_MAX_LENGTH }}
            sx={{ mt: 3 }}
            helperText={`Included in the email body. ${comment.length}/${COMMENT_MAX_LENGTH}`}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={sending}
        >
          Cancel
        </Button>
        <Button
          onClick={onSend}
          variant="contained"
          disabled={sending || finalRecipients.length === 0}
          startIcon={sending ? <CircularProgress size={20} /> : <EmailIcon />}
        >
          {sending ? 'Sending...' : 'Send Email'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailNotificationDialog;
